import { toast } from 'react-toastify';
import { SERVICES_API_BASE_URL } from './apiConfig';

const BASE_URL = `${SERVICES_API_BASE_URL}/api`;

// Helper to get authorization headers
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("token");
  if (!token) {
    console.error("No authentication token found.");
    return {};
  }
  return {
    "Content-Type": "application/json",
    "Authorization": `Bearer ${token}`
  };
};

// Generic API call wrapper
const apiCall = async (endpoint: string, method: string, data?: any) => {
  try {
    const headers = getAuthHeaders();
    const config: RequestInit = {
      method,
      headers,
    };

    if (data) {
      config.body = JSON.stringify(data);
    }

    const response = await fetch(`${BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || "Something went wrong.");
    } else if (response.status === 401) {
      toast.error("Unauthorized. Please log in again.");
      throw new Error("Unauthorized");
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error: any) {
    console.error(`API Error (${method} ${endpoint}):`, error);
    toast.error(error.message || "Something went wrong. Please try again.");
    throw error;
  }
};

export enum ProposalStatus {
  Draft = 0,
  Sent = 1,
  Accepted = 2,
  Declined = 3,
  Expired = 4
}

export interface Proposal {
  id: string;
  title: string;
  customerName: string;
  customerEmail: string;
  status: ProposalStatus;
  validDays: number;
  expiryDate: string;
  content: string;
  publicToken: string;
}

export interface CreateProposalRequest {
  title: string;
  customerName: string;
  customerEmail: string;
  validDays: number;
  content: string;
  sendEmail?: boolean;
}

export interface AcceptProposalRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  companyName: string;
  agreedToTerms: boolean;
  signature: string; // base64
}

export const proposalApi = {
  getProposals: async (): Promise<Proposal[]> => {
    return apiCall("/proposal", "GET");
  },
  getProposalById: async (id: string): Promise<Proposal> => {
    return apiCall(`/proposal/${id}`, "GET");
  },
  createProposal: async (data: CreateProposalRequest): Promise<Proposal> => {
    return apiCall("/proposal", "POST", data);
  },
  updateProposal: async (id: string, data: CreateProposalRequest): Promise<Proposal> => {
    return apiCall(`/proposal/${id}`, "PUT", data);
  },
  deleteProposal: async (id: string): Promise<void> => {
    return apiCall(`/proposal/${id}`, "DELETE");
  },
  sendProposal: async (id: string): Promise<void> => {
    return apiCall(`/proposal/${id}/send`, "POST");
  },
  getPublicProposal: async (token: string): Promise<Proposal> => {
    // Note: getAuthHeaders will log an error to console for public users, but will still return an empty header object.
    return apiCall(`/proposal/public/${token}`, "GET");
  },
  acceptProposal: async (token: string, data?: AcceptProposalRequest): Promise<void> => {
    return apiCall(`/proposal/accept/${token}`, "POST", data);
  },
  declineProposal: async (token: string): Promise<void> => {
    return apiCall(`/proposal/decline/${token}`, "POST");
  },
};